import { formatDistanceToNow } from "date-fns";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import type { BotCommentRecord } from "@/react/hooks/agent_profile/useBotCommentsInfinite";
import { DisplayTiptapContent } from "@circle-react/components/CommentsV3/Comment/DisplayTiptapContent";
import { DisplayTrixContent } from "@circle-react-shared/uikit/DisplayTrixContent";
import { Loader } from "@circle-react-shared/uikit/Loader";

interface CommentsListProps {
  comments: BotCommentRecord[];
  error?: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isLoading: boolean;
  scrollTargetId: string;
}

const formatTimeAgo = (date: Date) => {
  const formatted = formatDistanceToNow(date, { addSuffix: true });
  return formatted
    .replace("about ", "")
    .replace("less than a minute ago", "1m ago")
    .replace(/(\d+) minutes? ago/, "$1m ago")
    .replace(/(\d+) hours? ago/, "$1h ago")
    .replace(/(\d+) days? ago/, "$1d ago")
    .replace(/(\d+) months? ago/, "$1mo ago")
    .replace(/(\d+) years? ago/, "$1y ago");
};

export const CommentsList = ({
  comments,
  fetchNextPage,
  hasNextPage,
  isLoading,
  error,
  scrollTargetId,
}: CommentsListProps) => {
  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-32 flex-col items-center justify-center px-6 text-center">
        <p className="text-sm text-[#dc2626]">
          {t("community_inbox.profile.error_loading_comments")}
        </p>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="flex h-32 flex-col items-center justify-center px-6 text-center">
        <p className="text-sm text-[#717680]">
          {t("community_inbox.profile.no_comments")}
        </p>
      </div>
    );
  }

  return (
    <InfiniteScroll
      style={{ overflow: "initial" }}
      scrollThreshold={0.5}
      next={fetchNextPage}
      hasMore={hasNextPage}
      loader={
        <div className="px-6">
          <Loader />
        </div>
      }
      dataLength={comments.length}
      scrollableTarget={scrollTargetId}
      className=""
    >
      {comments.map(comment => (
        <Link
          key={comment.id}
          to={`/c/${comment.space_slug}/${comment.post_slug}#comment_wrapper_${comment.id}`}
          className="flex flex-col items-start gap-2 self-stretch border-b border-[#e4e7eb] px-6 py-4"
        >
          <div>
            <span className="text-sm font-normal text-[#717680]">
              {t("community_inbox.profile.commented_on")}{" "}
            </span>
            <span className="text-sm font-medium text-[#191b1f]">
              {comment.post_name || comment.post_truncated_content}
            </span>
            <span className="mx-1.5 inline-flex items-center align-middle">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="5"
                height="5"
                viewBox="0 0 5 5"
                fill="none"
                className="inline-block"
              >
                <circle cx="2.5" cy="2.5" r="2.5" fill="#E4E7EB" />
              </svg>
            </span>
            <span className="text-sm font-normal text-[#717680]">
              {formatTimeAgo(new Date(comment.created_at))}
            </span>
          </div>
          <div className="text-sm font-normal leading-5 text-[#42464d]">
            {comment.editor === "tiptap" ? (
              <DisplayTiptapContent
                key={comment.updated_at}
                tiptapBody={comment.tiptap_body}
              />
            ) : (
              <DisplayTrixContent content={comment.body_trix_content || ""} />
            )}
          </div>
        </Link>
      ))}
    </InfiniteScroll>
  );
};
